import * as React from 'react';
import { useSelector } from 'react-redux';

import { MODULES } from '../../../../common/constants/map';
import { useApplicationContext } from '../../../../common/containers/ApplicationContext';
import { loadYMapsApi } from '../../../../common/utils/loadYMapsApi';
import { IMapStatePayload, MapController } from '../../../controller/MapController';
import { selectMapBounds } from '../../../selectors';
import { getMapConstructorOptions, MAP_DEFAULT_STATE, MAP_VISIBLE_RESTRICT_AREA } from '../constants';

interface IUseYMapsInitializationParams {
  elementRef: React.RefObject<HTMLDivElement | null>;
  onUserIntent: (payload: IMapStatePayload) => void;
}

interface IUseYMapsInitializationResult {
  ymaps: YMaps.IYMaps | undefined;
  map: YMaps.Map | undefined;
  mapController: MapController | undefined;
}

export function useYMapsInitialization({
  elementRef,
  onUserIntent,
}: IUseYMapsInitializationParams): IUseYMapsInitializationResult {
  const applicationContext = useApplicationContext();
  const initialBounds = useSelector(selectMapBounds);
  const options = applicationContext.config.get<YMaps.IMapOptions>(
    'dailyrentSearchFrontend.ymapsMapConstructorOptions',
  );

  const [ymaps, setYMaps] = React.useState<YMaps.IYMaps>();
  const [map, setMap] = React.useState<YMaps.Map>();
  const [mapController, setMapController] = React.useState<MapController>();

  React.useEffect(() => {
    const ignoreLoadFailure = (): void => undefined;

    try {
      loadYMapsApi(MODULES).then(setYMaps, ignoreLoadFailure);
    } catch (e) {
      ignoreLoadFailure();
    }
  }, []);

  React.useEffect(() => {
    if (!ymaps) {
      return;
    }

    const mapInstance = new ymaps.Map(
      elementRef.current as HTMLDivElement,
      { ...MAP_DEFAULT_STATE, ...initialBounds },
      getMapConstructorOptions(options),
    );

    const controllerInstance = new MapController({
      map: mapInstance,
      onUserIntent,
      visibleRestrictArea: MAP_VISIBLE_RESTRICT_AREA,
    });

    setMap(mapInstance);
    setMapController(controllerInstance);

    return () => {
      controllerInstance.destroy();
      mapInstance.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymaps]);

  return { ymaps, map, mapController };
}
